import React from 'react'
import PropTypes from 'prop-types'
import withReduxForm from './withReduxFormField'
import { Field } from 'redux-form'
import CtaButton from '../buttons/cta-button'
import FlatButton from '../buttons/flat-button'
import FieldBlock from '../forms/field-block'
import InputField from '../forms/input-field'
import { GridRow, GridCol } from '../grid/'
import Textarea from '../forms/textarea'
import Label from '../texts/label'
import SmallText from '../texts/small-text'
import InfoBlock from '../texts/info-block'
import IconButton from '../buttons/icon-button'
import CharityImageGallery from '../../services/mycharity/media/charity-image-gallery'
import Notification from '../notifications/notification'
import ModalWrapper from '../modal/modal-wrapper'
import ContactForm from '../../services/mycharity/charity/ContactForm'
import ContactsList from '../contacts/contact-list'
import DeleteContactModal from '../../services/mycharity/charity/DeleteContactModal'

const LabelledInput = withReduxForm(InputField)
const RegularTextarea = withReduxForm(Textarea)

const CharityInfoForm = props => {
  const {
    handleSubmit,
    handleCancel,
    submitting,
    submitFailed,
    invalid,
    showContactForm,
    onAddContact,
    onOpenContactFormModal,
    onCloseContactFormModal,
    onOpenDeleteContactModal,
    charityContacts,
    titleText,
    charityNameText,
    shortDescriptionText,
    shortDescriptionHelperText,
    descriptionText,
    descriptionHelperText,
    coverImageText,
    coverImageHelperText,
    socialMediaText,
    facebookLinkText,
    twitterLinkText,
    instagramLinkText,
    googlePlusLinkText,
    contactText,
    yourWebsiteText,
    contactBlockText,
    contactBlockHelperText,
    addContactDataText,
    optionalText,
    saveText,
    cancelText,
    errorNotificationText
  } = props
  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FieldBlock className='label-space' titleLabel={titleText}>
          <Field
            name='name'
            id='name'
            label={charityNameText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
            autoFocus
          />
          <Field
            name='shortDescription'
            id='short-description'
            theme='dark-theme'
            label={shortDescriptionText}
            helperText={shortDescriptionHelperText}
            component={RegularTextarea}
          />
          <Field
            name='description'
            id='description'
            theme='dark-theme'
            label={descriptionText}
            helperText={descriptionHelperText}
            component={RegularTextarea}
          />
        </FieldBlock>
        <FieldBlock
          shyLabel
          titleLabel={coverImageText}
          hasNote
          note={`(${optionalText})`}
          smallText={coverImageHelperText}
        >
          <Field
            name='photo'
            iconName='image'
            theme='dark-theme'
            text='Upload image'
            component={CharityImageGallery}
            charityId={props.charity.id}
            maxImages={1}
            type='text'
          />
        </FieldBlock>
        <FieldBlock
          className='label-space'
          titleLabel={socialMediaText}
          hasNote
          note={`(${optionalText})`}
        >
          <Field
            name='facebook'
            id='facebook-link'
            iconName='social-facebook'
            label={facebookLinkText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
          />
          <Field
            name='twitter'
            id='twitter-link'
            iconName='social-twitter'
            label={twitterLinkText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
          />
          <Field
            name='instagram'
            id='instagram-link'
            iconName='social-instagram'
            label={instagramLinkText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
          />
          <Field
            name='googlePlus'
            id='google-link'
            iconName='social-google'
            label={googlePlusLinkText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
          />
        </FieldBlock>
        <FieldBlock
          className='label-space'
          titleLabel={contactText}
          hasNote
          note={`(${optionalText})`}
        >
          <Field
            name='website'
            id='website'
            label={yourWebsiteText}
            theme='dark-theme'
            component={LabelledInput}
            type='text'
          />
        </FieldBlock>
        <FieldBlock hasHeader={false}>
          <InfoBlock>
            <Label shyLabel>{contactBlockText}</Label>
            <SmallText>{contactBlockHelperText}</SmallText>
          </InfoBlock>
          <ContactsList
            contacts={charityContacts}
            isEditable={true}
            onEditContact={onOpenContactFormModal}
            onDeleteContact={onOpenDeleteContactModal}
          />
          <IconButton
            upperValue
            iconName='plus'
            type='button'
            text={addContactDataText}
            onClick={onAddContact}
          />
        </FieldBlock>
        {submitFailed &&
          invalid && (
            <Notification status='error' iconName='warning' hasIcon>
              {errorNotificationText}
            </Notification>
          )}
        <GridRow className='bottom-space top-space'>
          <GridCol type='one-half'>
            <FlatButton
              text={cancelText}
              isInverse
              type='button'
              onClick={handleCancel}
              disabled={submitting}
            />
          </GridCol>
          <GridCol type='one-half pull-right'>
            <CtaButton
              hasIcon={false}
              text={saveText}
              type='submit'
              disabled={submitting}
              isLoading={submitting}
            />
          </GridCol>
        </GridRow>
      </form>
      {showContactForm && (
        <ModalWrapper onClose={onCloseContactFormModal}>
          <ContactForm />
        </ModalWrapper>
      )}
      <DeleteContactModal />
    </div>
  )
}

CharityInfoForm.propTypes = {
  handleSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  submitFailed: PropTypes.bool,
  invalid: PropTypes.bool,
  autoFocusField: PropTypes.string,
  showContactForm: PropTypes.bool,
  onAddContact: PropTypes.func,
  onOpenContactFormModal: PropTypes.func,
  onCloseContactFormModal: PropTypes.func,
  onOpenDeleteContactModal: PropTypes.func,
  charityContacts: PropTypes.arrayOf(PropTypes.object),
  // translations
  titleText: PropTypes.string,
  charityNameText: PropTypes.string,
  shortDescriptionText: PropTypes.string,
  shortDescriptionHelperText: PropTypes.string,
  descriptionText: PropTypes.string,
  descriptionHelperText: PropTypes.string,
  coverImageText: PropTypes.string,
  coverImageHelperText: PropTypes.string,
  socialMediaText: PropTypes.string,
  facebookLinkText: PropTypes.string,
  twitterLinkText: PropTypes.string,
  instagramLinkText: PropTypes.string,
  googlePlusLinkText: PropTypes.string,
  contactText: PropTypes.string,
  yourWebsiteText: PropTypes.string,
  contactBlockText: PropTypes.string,
  contactBlockHelperText: PropTypes.string,
  addContactDataText: PropTypes.string,
  optionalText: PropTypes.string,
  cancelText: PropTypes.string,
  saveText: PropTypes.string,
  errorNotificationText: PropTypes.string
}

export default CharityInfoForm



// WEBPACK FOOTER //
// ./src/ui/forms/charity-info-form.js